import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { createProject, updateProject, deleteProject } from '../../services/expenseFirestore';
import { PROJECT_STATUSES, PROJECT_SERVICES, STATUS_COLORS } from '../../constants/expenseConstants';
import { rowTotal } from '../../utils/invoiceUtils';
import DataTable from '../ui/DataTable';
import Modal from '../ui/Modal';
import ConfirmModal from '../ui/ConfirmModal';
import Badge from '../ui/Badge';

const EMPTY = {
  projectId: '',
  projectName: '',
  startDate: '',
  projectValue: '',
  client: '',
  services: [],
  status: 'Pending',
  billedValue: '',
};

export default function ProjectManagement({ projects, loading }) {
  const [form, setForm] = useState(EMPTY);
  const [editModal, setEditModal] = useState(false);
  const [statusModal, setStatusModal] = useState(null);
  const [viewAdditionalModal, setViewAdditionalModal] = useState(null);
  const [filterModal, setFilterModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const [filters, setFilters] = useState({
    status: '',
    client: '',
    service: '',
  });

  const uniqueClients = useMemo(() => {
    const clients = new Set(projects.map(p => p.client).filter(c => c?.trim()));
    return Array.from(clients).sort();
  }, [projects]);

  const filteredAndSortedProjects = useMemo(() => {
    let filtered = projects;
    
    if (filters.status) {
      filtered = filtered.filter(p => p.status === filters.status);
    }
    if (filters.client) {
      filtered = filtered.filter(p => p.client === filters.client);
    }
    if (filters.service) {
      filtered = filtered.filter(p => {
        const pServices = (p.services || []).map(s => typeof s === 'string' ? s : s.service);
        return pServices.includes(filters.service);
      });
    }

    return filtered.sort((a, b) => {
      const getRank = (status) => {
        if (status === 'In Progress') return 0;
        if (status === 'Completed') return 2;
        return 1; // Pending, On Hold, Cancelled
      };
      
      const rankA = getRank(a.status);
      const rankB = getRank(b.status);
      
      if (rankA !== rankB) return rankA - rankB;
      // Secondary sort by date (newest first) or project ID if needed
      return (b.createdAt || 0) - (a.createdAt || 0);
    });
  }, [projects, filters]);

  const openAdd = () => { setForm(EMPTY); setEditingId(null); setEditModal(true); };
  const openEdit = (p) => {
    const formattedServices = (p.services || []).map(s => {
      if (typeof s === 'string') return { service: s, amount: '' };
      return s;
    });
    setForm({
      projectId: p.projectId || '',
      projectName: p.projectName || '',
      startDate: p.startDate || '',
      projectValue: p.projectValue || '',
      client: p.client || '',
      services: formattedServices,
      status: p.status || 'Pending',
      billedValue: p.billedValue || '',
    });
    setEditingId(p.id);
    setEditModal(true);
  };

  const handleSave = async () => {
    if (!form.projectId?.trim() || !form.projectName?.trim()) {
      toast.warning('Project ID and Name are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateProject(editingId, form);
        toast.success('Project updated');
      } else {
        await createProject(form);
        toast.success('Project created');
      }
      setEditModal(false);
      setForm(EMPTY);
      setEditingId(null);
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!statusModal || !newStatus) return;
    setSaving(true);
    try {
      await updateProject(statusModal.id, { status: newStatus, projectName: statusModal.projectName });
      toast.success('Status updated');
      setStatusModal(null);
    } catch (e) {
      toast.error(e.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProject(deleteTarget.id);
      toast.success('Project deleted');
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const columns = [
    { key: 'projectId', label: 'Project ID' },
    { key: 'projectName', label: 'Project Name' },
    { key: 'startDate', label: 'Start' },
    { key: 'projectValue', label: 'Value', render: (r) => `₹ ${Number(r.projectValue || 0).toLocaleString('en-IN')}` },
    { key: 'client', label: 'Client' },
    {
      key: 'status',
      label: 'Status',
      render: (r) => (
        <button type="button" className="exp-status-btn" onClick={() => { setStatusModal(r); setNewStatus(r.status); }}>
          <Badge variant={STATUS_COLORS[r.status] || 'default'}>{r.status}</Badge>
        </button>
      ),
    },
    { key: 'services', label: 'Service', render: (r) => (r.services || []).map(s => typeof s === 'string' ? s : s.service).join(', ') || '—' },
    { 
      key: 'billedValue', 
      label: 'Billed Value', 
      render: (r) => {
        const pVal = Number(r.projectValue || 0);
        const bVal = Number(r.billedValue || 0);
        
        if (bVal > 0 && pVal !== bVal && r.invoiceDraft?.additionalRows?.length) {
          return (
            <button 
              type="button" 
              className="ui-btn ui-btn-secondary" 
              style={{ padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: 'bold' }}
              onClick={() => setViewAdditionalModal(r)}
            >
              ₹{pVal.toLocaleString('en-IN')} &rarr; ₹{bVal.toLocaleString('en-IN')}
            </button>
          );
        }
        
        return `₹ ${bVal.toLocaleString('en-IN')}`;
      } 
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="exp-actions">
          <button type="button" className="exp-icon-btn" onClick={() => openEdit(r)} title="Edit"><i className="fas fa-edit" /></button>
          <button type="button" className="exp-icon-btn danger" onClick={() => setDeleteTarget(r)} title="Delete"><i className="fas fa-trash" /></button>
        </div>
      ),
    },
  ];

  return (
    <div className="expense-panel">
      <div className="expense-card-header">
        <h3 className="expense-panel-title">Project Management</h3>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setFilterModal(true)}>
            <i className="fas fa-filter" /> Filter
          </button>
          <button type="button" className="btn-primary" onClick={openAdd}>
            <i className="fas fa-plus" /> Add Project
          </button>
        </div>
      </div>
      {loading ? <p className="expense-loading">Loading projects…</p> : (
        <DataTable columns={columns} data={filteredAndSortedProjects} searchKeys={['projectId', 'projectName', 'client']} searchPlaceholder="Search projects…" />
      )}

      <Modal open={filterModal} onClose={() => setFilterModal(false)} title="Filter Projects"
        footer={<>
          <button type="button" className="ui-btn ui-btn-secondary" onClick={() => {
            setFilters({ status: '', client: '', service: '' });
            setFilterModal(false);
          }}>Clear Filters</button>
          <button type="button" className="ui-btn ui-btn-primary" onClick={() => setFilterModal(false)}>Apply</button>
        </>}
      >
        <div className="ui-form-row">
          <div className="ui-form-group">
            <label>Status</label>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">All Statuses</option>
              {PROJECT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="ui-form-group">
            <label>Client</label>
            <select value={filters.client} onChange={(e) => setFilters({ ...filters, client: e.target.value })}>
              <option value="">All Clients</option>
              {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="ui-form-group">
          <label>Service</label>
          <select value={filters.service} onChange={(e) => setFilters({ ...filters, service: e.target.value })}>
            <option value="">All Services</option>
            {PROJECT_SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </Modal>

      <Modal open={editModal} onClose={() => setEditModal(false)} title={editingId ? 'Edit Project' : 'Add Project'} size="lg"
        footer={<>
          <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setEditModal(false)}>Cancel</button>
          <button type="button" className="ui-btn ui-btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </>}
      >
        <div className="ui-form-row">
          <div className="ui-form-group"><label>Project ID *</label><input value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })} placeholder="PRJ-001" /></div>
          <div className="ui-form-group"><label>Project Name *</label><input value={form.projectName} onChange={(e) => setForm({ ...form, projectName: e.target.value })} /></div>
        </div>
        <div className="ui-form-row">
          <div className="ui-form-group"><label>Start Date</label><input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></div>
          <div className="ui-form-group"><label>Project Value (₹)</label><input type="number" value={form.projectValue} onChange={(e) => setForm({ ...form, projectValue: e.target.value })} /></div>
        </div>
        <div className="ui-form-row">
          <div className="ui-form-group"><label>Client</label><input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} /></div>
          <div className="ui-form-group">
            <label>Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="ui-form-group"><label>Billed Value (₹)</label><input type="number" value={form.billedValue} onChange={(e) => setForm({ ...form, billedValue: e.target.value })} /></div>
        <div className="ui-form-group" style={{ gridColumn: '1 / -1' }}>
          <label>Services</label>
          <div style={{ marginBottom: '1rem' }}>
            <select 
              onChange={(e) => {
                const svc = e.target.value;
                if (!svc) return;
                if (!form.services.find(s => s.service === svc)) {
                  setForm(f => ({ ...f, services: [...f.services, { service: svc, amount: '' }] }));
                }
                e.target.value = '';
              }}
              defaultValue=""
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value="" disabled>Select a service to add...</option>
              {PROJECT_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          
          {form.services.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1rem', fontWeight: 'bold' }}>
                <span>Service</span>
                <span>Amount (₹)</span>
                <span>Action</span>
              </div>
              {form.services.map((item, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1rem', alignItems: 'center' }}>
                  <input readOnly value={item.service} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#f9fafb' }} />
                  <input 
                    type="number" 
                    placeholder="Enter Amount" 
                    value={item.amount} 
                    onChange={(e) => {
                      const newServices = [...form.services];
                      newServices[idx].amount = e.target.value;
                      setForm(f => ({ ...f, services: newServices }));
                    }}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                  <button 
                    type="button" 
                    className="exp-icon-btn danger" 
                    onClick={() => {
                      const newServices = form.services.filter((_, i) => i !== idx);
                      setForm(f => ({ ...f, services: newServices }));
                    }}
                  >
                    <i className="fas fa-trash" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <Modal open={!!statusModal} onClose={() => setStatusModal(null)} title="Project Status"
        footer={<>
          <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setStatusModal(null)}>Cancel</button>
          <button type="button" className="ui-btn ui-btn-primary" onClick={handleStatusUpdate} disabled={saving}>Update Status</button>
        </>}
      >
        {statusModal && (
          <>
            <div className="exp-status-detail"><strong>Project:</strong> {statusModal.projectName}</div>
            <div className="exp-status-detail"><strong>Services:</strong> {(statusModal.services || []).map(s => typeof s === 'string' ? s : s.service).join(', ') || '—'}</div>
            <div className="exp-status-detail"><strong>Project Value:</strong> ₹ {Number(statusModal.projectValue || 0).toLocaleString('en-IN')}</div>
            <div className="exp-status-detail"><strong>Start Date:</strong> {statusModal.startDate || '—'}</div>
            <div className="exp-status-detail"><strong>Current Status:</strong> <Badge variant={STATUS_COLORS[statusModal.status]}>{statusModal.status}</Badge></div>
            {statusModal.status === 'Completed' && (
              <>
                <div className="exp-status-detail"><strong>Completed Date:</strong> {statusModal.completedDate || '—'}</div>
                <div className="exp-status-detail"><strong>Updated Date:</strong> {statusModal.updatedAt?.toDate?.()?.toLocaleDateString?.() || statusModal.updatedAt || '—'}</div>
              </>
            )}
            <div className="ui-form-group" style={{ marginTop: '1rem' }}>
              <label>Change Status</label>
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </>
        )}
      </Modal>

      <Modal 
        open={!!viewAdditionalModal} 
        onClose={() => setViewAdditionalModal(null)} 
        title="Additional Invoice Rows"
        footer={
          <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setViewAdditionalModal(null)}>Close</button>
        }
      >
        {viewAdditionalModal && (
          <div className="exp-invoice-rows" style={{ marginTop: '1rem' }}>
            <div className="exp-invoice-row-header" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              <span>Particulars</span>
              <span style={{ textAlign: 'right' }}>Price</span>
              <span style={{ textAlign: 'center' }}>Qty</span>
              <span style={{ textAlign: 'right' }}>Total</span>
            </div>
            {viewAdditionalModal.invoiceDraft?.additionalRows?.map((row, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0.5rem', padding: '0.5rem 0', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ wordBreak: 'break-word' }}>{row.particulars}</span>
                <span style={{ textAlign: 'right' }}>₹ {Number(row.price).toLocaleString('en-IN')}</span>
                <span style={{ textAlign: 'center' }}>{row.qty}</span>
                <span style={{ textAlign: 'right', fontWeight: 'bold' }}>₹ {rowTotal(row).toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <ConfirmModal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete}
        title="Delete Project" message={`Delete project "${deleteTarget?.projectName}"? This cannot be undone.`} confirmLabel="Delete" />
    </div>
  );
}
